import Map from "ol/Map";
import View from "ol/View";
import { Tile as TileLayer, Vector as VectorLayer } from "ol/layer";
import WMTS, { optionsFromCapabilities } from "ol/source/WMTS";
import { Vector as VectorSource } from "ol/source";
import WMTSCapabilities from "ol/format/WMTSCapabilities";
import { Style, Stroke, Fill, Icon } from "ol/style";
import Feature from "ol/Feature";
import { Point } from "ol/geom";
import GeoJSON from "ol/format/GeoJSON";
import Projection from "ol/proj/Projection";
import { addProjection, get as getProjection } from "ol/proj";
import { register } from "ol/proj/proj4";
import { createEmpty, extend, isEmpty } from "ol/extent";
import { defaults as defaultControls } from "ol/control/defaults";
import { defaults as defaultInteractions } from "ol/interaction/defaults";
import Zoom from "ol/control/Zoom";
import Rotate from "ol/control/Rotate";
import ScaleLine from "ol/control/ScaleLine";
import proj4 from "proj4";

proj4.defs(
  "EPSG:3016",
  "+proj=tmerc +lat_0=0 +lon_0=20.25 +k=1 +x_0=150000 +y_0=0 +ellps=GRS80 +towgs84=0,0,0,0,0,0,0 +units=m +no_defs"
);
register(proj4);

const swerefExtent: [number, number, number, number] = [
  -93218.3385, 7034909.8738, 261434.625, 7744215.8,
];
// EPSG:3016 är officiellt north-east-up. WMTS-capabilities anger TopLeftCorner
// north-first; OL byter axlar bara om projektionens axisOrientation börjar med 'n'.
const sweref = new Projection({
  code: "EPSG:3016",
  extent: swerefExtent,
  units: "m",
  axisOrientation: "neu",
});
addProjection(sweref);
// Alias urn-form så optionsFromCapabilities (som läser SupportedCRS = urn:ogc:def:crs:EPSG::3016)
// hittar samma projektion.
const swerefUrn = new Projection({
  code: "urn:ogc:def:crs:EPSG::3016",
  extent: swerefExtent,
  units: "m",
  axisOrientation: "neu",
});
addProjection(swerefUrn);

const WMTS_CAPABILITIES_URL =
  "https://wms.umea.se/geoserver/gwc/service/wmts?REQUEST=GetCapabilities";
const WMTS_LAYER = "Umea:Startkarta";
const WMTS_MATRIX_SET = "Sweref992015";

const polygonSource = new VectorSource();
const schoolSource = new VectorSource();
const addressSource = new VectorSource();

interface BusskortMapType {
  map: Map | null;
  init: (elementId: string) => Promise<void>;
  showPolygon: (geoJson: string, schoolLat: number | null, schoolLon: number | null) => void;
  showAddress: (x: number, y: number, eligible: boolean) => void;
  fitToAll: () => void;
  clearAll: () => void;
}

const BusskortMap: BusskortMapType = {
  map: null,

  async init(elementId: string): Promise<void> {
    if (this.map) {
      this.map.setTarget(undefined);
    }

    // Build the basemap layer from WMTS GetCapabilities — gives us cached GWC tiles
    // with a tile grid that exactly matches the server, instead of guessing resolutions.
    const wmtsLayer = await buildBasemapLayer();

    const polygonLayer = new VectorLayer({
      source: polygonSource,
      style: new Style({
        stroke: new Stroke({ color: "#CC0000", width: 2 }),
        fill: new Fill({ color: "rgba(255,0,0,0.2)" }),
      }),
    });

    const schoolLayer = new VectorLayer({ source: schoolSource });
    const addressLayer = new VectorLayer({ source: addressSource });

    this.map = new Map({
      layers: [wmtsLayer, polygonLayer, schoolLayer, addressLayer],
      target: elementId,
      pixelRatio: 1,
      controls: defaultControls({ zoom: false, attribution: false, rotate: false }).extend([
        new Zoom({ className: "ol-zoom ol-zoom-right" }),
        new Rotate({ autoHide: true, label: "⬆", tipLabel: "Återställ norr" }),
        new ScaleLine({ units: "metric" }),
      ]),
      interactions: defaultInteractions({ pinchRotate: true }),
      view: new View({
        projection: sweref,
        center: [149808.62, 7079466.82],
        zoom: 7,
        minZoom: 4,
        extent: swerefExtent,
      }),
    });
  },

  showPolygon(geoJson: string, schoolLat: number | null, schoolLon: number | null): void {
    polygonSource.clear();
    schoolSource.clear();
    addressSource.clear();

    if (!geoJson) return;

    const format = new GeoJSON();
    const features = format.readFeatures(JSON.parse(geoJson), {
      dataProjection: "EPSG:3016",
      featureProjection: "EPSG:3016",
    });
    polygonSource.addFeatures(features);

    // Skolmarkören placeras på skolans egen punkt (EPSG:3016, från skoltabellen).
    // Saknas punkten ritas ingen markör — polygonens mittpunkt är inte skolans läge.
    if (schoolLon !== null && schoolLat !== null) {
      const schoolFeature = new Feature({
        geometry: new Point([schoolLon, schoolLat]),
      });
      schoolFeature.setStyle(
        new Style({
          image: new Icon({
            src: "img/vald-skola.svg",
            anchor: [0.5, 1],
            scale: 1,
          }),
        })
      );
      schoolSource.addFeature(schoolFeature);
    }

    const fitExtent = polygonSource.getExtent();
    if (fitExtent) {
      this.map!.getView().fit(fitExtent, {
        padding: [80, 80, 80, 300],
        duration: 400,
      });
    }
  },

  showAddress(x: number, y: number, _eligible: boolean): void {
    addressSource.clear();

    if (!x || !y) return;

    const feature = new Feature({
      geometry: new Point([x, y]),
    });

    feature.setStyle(
      new Style({
        image: new Icon({
          src: "img/din-adress.svg",
          anchor: [0.5, 1],
          scale: 1,
        }),
      })
    );

    addressSource.addFeature(feature);
    this.fitToAll();
  },

  fitToAll(): void {
    const ext = createEmpty();
    if (polygonSource.getFeatures().length > 0) {
      const polygonExtent = polygonSource.getExtent();
      if (polygonExtent) extend(ext, polygonExtent);
    }
    if (addressSource.getFeatures().length > 0) {
      const addressExtent = addressSource.getExtent();
      if (addressExtent) extend(ext, addressExtent);
    }
    if (!isEmpty(ext)) {
      this.map!.getView().fit(ext, {
        padding: [80, 80, 80, 300],
        duration: 400,
      });
    }
  },

  clearAll(): void {
    polygonSource.clear();
    schoolSource.clear();
    addressSource.clear();
    this.map!.getView().animate({
      center: [149808.62, 7079466.82],
      zoom: 7,
      duration: 300,
    });
  },
};

async function buildBasemapLayer(): Promise<TileLayer<WMTS>> {
  const response = await fetch(WMTS_CAPABILITIES_URL);
  if (!response.ok) {
    throw new Error(`WMTS GetCapabilities failed: ${response.status}`);
  }
  const text = await response.text();
  const parser = new WMTSCapabilities();
  const capabilities = parser.read(text);

  const wmtsOptions = optionsFromCapabilities(capabilities, {
    layer: WMTS_LAYER,
    matrixSet: WMTS_MATRIX_SET,
  });
  if (!wmtsOptions) {
    throw new Error(
      `WMTS layer "${WMTS_LAYER}" or matrix set "${WMTS_MATRIX_SET}" not found in GetCapabilities`
    );
  }

  // Force the projection to our registered EPSG:3016 instance — capabilities use the urn form
  // but the View uses the short form, and OL needs them to resolve to the same projection.
  const projection = getProjection("EPSG:3016") ?? sweref;

  return new TileLayer({
    extent: swerefExtent,
    preload: 0,
    source: new WMTS({
      ...wmtsOptions,
      projection,
      crossOrigin: "anonymous",
    }),
  });
}

(window as unknown as Record<string, unknown>).BusskortMap = BusskortMap;
export { BusskortMap };
